import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Snackbar, Alert, Chip, LinearProgress, Tabs, Tab,
  Card, CardContent, Grid, Switch, FormControlLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon, Search as SearchIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Restaurant as RestaurantIcon, Schedule as ScheduleIcon,
  LocalDining as DiningIcon
} from '@mui/icons-material';
import { mealMenuAPI } from '../api/api';

const MealMenu = () => {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date: '',
    mealType: '',
    active: ''
  });
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openMealDialog, setOpenMealDialog] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [newMeal, setNewMeal] = useState({
    date: '',
    mealType: 'Breakfast',
    mainDish: '',
    sideDish: '',
    soup: '',
    salad: '',
    dessert: '',
    drink: '',
    calories: '',
    price: '',
    active: true,
    notes: ''
  });

  const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
  const daysOfWeek = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchMeals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await mealMenuAPI.getAll(filters);
      setMeals(response.data || []);
    } catch (error) {
      console.error('Error fetching meals:', error);
      showSnackbar('Yemek menüsü yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showSnackbar]);

  useEffect(() => {
    fetchMeals();
  }, [fetchMeals]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    fetchMeals();
  };

  const handleRefresh = () => {
    fetchMeals();
  };

  const handleOpenMealDialog = (meal = null) => {
    setEditingMeal(meal);
    if (meal) {
      setNewMeal({
        date: meal.date || '',
        mealType: meal.mealType || 'Breakfast',
        mainDish: meal.mainDish || '',
        sideDish: meal.sideDish || '',
        soup: meal.soup || '',
        salad: meal.salad || '',
        dessert: meal.dessert || '',
        drink: meal.drink || '',
        calories: meal.calories || '',
        price: meal.price || '',
        active: meal.active !== false,
        notes: meal.notes || ''
      });
    } else {
      setNewMeal({
        date: new Date().toISOString().split('T')[0],
        mealType: 'Breakfast',
        mainDish: '',
        sideDish: '',
        soup: '',
        salad: '',
        dessert: '',
        drink: '',
        calories: '',
        price: '',
        active: true,
        notes: ''
      });
    }
    setOpenMealDialog(true);
  };

  const handleCloseMealDialog = () => {
    setOpenMealDialog(false);
    setEditingMeal(null);
    setNewMeal({
      date: '',
      mealType: 'Breakfast',
      mainDish: '',
      sideDish: '',
      soup: '',
      salad: '',
      dessert: '',
      drink: '',
      calories: '',
      price: '',
      active: true,
      notes: ''
    });
  };

  const handleMealChange = (field, value) => {
    setNewMeal(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveMeal = async () => {
    if (!newMeal.date || !newMeal.mainDish) {
      showSnackbar('Lütfen zorunlu alanları doldurun', 'error');
      return;
    }

    try {
      if (editingMeal) {
        await mealMenuAPI.update(editingMeal.id, newMeal);
        showSnackbar('Yemek menüsü güncellendi', 'success');
      } else {
        await mealMenuAPI.create(newMeal);
        showSnackbar('Yemek menüsü eklendi', 'success');
      }
      fetchMeals();
      handleCloseMealDialog();
    } catch (error) {
      console.error('Error saving meal:', error);
      showSnackbar('Kayıt sırasında hata oluştu', 'error');
    }
  };

  const handleDeleteMeal = async (id) => {
    if (window.confirm('Bu yemek menüsünü silmek istediğinize emin misiniz?')) {
      try {
        await mealMenuAPI.delete(id);
        showSnackbar('Yemek menüsü silindi', 'success');
        fetchMeals();
      } catch (error) {
        console.error('Error deleting meal:', error);
        showSnackbar('Silme sırasında hata oluştu', 'error');
      }
    }
  };

  const getMealTypeColor = (type) => {
    const colors = {
      'Breakfast': 'primary',
      'Lunch': 'success',
      'Dinner': 'warning',
      'Snack': 'info'
    };
    return colors[type] || 'default';
  };

  const getWeeklyStats = () => {
    const stats = {};
    meals.forEach(meal => {
      const day = new Date(meal.date).toLocaleDateString('tr-TR', { weekday: 'long' });
      if (!stats[day]) stats[day] = 0;
      stats[day]++;
    });
    return daysOfWeek.map(day => ({ day, count: stats[day] || 0 }));
  };

  const getMealTypeStats = () => {
    const stats = {};
    meals.forEach(meal => {
      stats[meal.mealType] = (stats[meal.mealType] || 0) + 1;
    });
    return Object.entries(stats).map(([name, value]) => ({ name, value }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Yemek Menüsü Yönetimi</Typography>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Menü Listesi" icon={<DiningIcon />} />
        <Tab label="İstatistikler" icon={<ScheduleIcon />} />
        <Tab label="Yeni Menü" icon={<AddIcon />} />
      </Tabs>

      {/* Filters */}
      {activeTab === 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Tarih"
                type="date"
                value={filters.date}
                onChange={(e) => handleFilterChange('date', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Öğün Tipi</InputLabel>
                <Select
                  value={filters.mealType}
                  onChange={(e) => handleFilterChange('mealType', e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {mealTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={filters.active}
                  onChange={(e) => handleFilterChange('active', e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="true">Aktif</MenuItem>
                  <MenuItem value="false">Pasif</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                >
                  Ara
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                >
                  Yenile
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Meals Table */}
      {activeTab === 0 && (
        <Paper sx={{ mb: 3 }}>
          {loading && <LinearProgress />}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tarih</TableCell>
                <TableCell>Öğün Tipi</TableCell>
                <TableCell>Ana Yemek</TableCell>
                <TableCell>Yardımcı Yemek</TableCell>
                <TableCell>Çorba</TableCell>
                <TableCell>Salata</TableCell>
                <TableCell>Tatlı</TableCell>
                <TableCell>İçecek</TableCell>
                <TableCell>Kalori</TableCell>
                <TableCell>Fiyat</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {meals.map((meal) => (
                <TableRow key={meal.id}>
                  <TableCell>{meal.date?.split('T')[0]}</TableCell>
                  <TableCell>
                    <Chip 
                      label={meal.mealType} 
                      color={getMealTypeColor(meal.mealType)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{meal.mainDish}</TableCell>
                  <TableCell>{meal.sideDish}</TableCell>
                  <TableCell>{meal.soup}</TableCell>
                  <TableCell>{meal.salad}</TableCell>
                  <TableCell>{meal.dessert}</TableCell>
                  <TableCell>{meal.drink}</TableCell>
                  <TableCell>{meal.calories}</TableCell>
                  <TableCell>{meal.price} TL</TableCell>
                  <TableCell>
                    <Chip 
                      label={meal.active ? 'Aktif' : 'Pasif'} 
                      color={meal.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenMealDialog(meal)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteMeal(meal.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {meals.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Menü bulunamadı</Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Statistics */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Öğün Tipi Dağılımı</Typography>
                <Box sx={{ height: 300 }}>
                  {getMealTypeStats().map((item, index) => (
                    <Box key={item.name} sx={{ mb: 1 }}>
                      <Typography variant="body2">{item.name}: {item.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Haftalık Dağılım</Typography>
                <Box sx={{ height: 300 }}>
                  {getWeeklyStats().map((item, index) => (
                    <Box key={item.day} sx={{ mb: 1 }}>
                      <Typography variant="body2">{item.day}: {item.count} menü</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* New Meal */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {editingMeal ? 'Menü Düzenle' : 'Yeni Menü Ekle'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Tarih"
                type="date"
                value={newMeal.date}
                onChange={(e) => handleMealChange('date', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Öğün Tipi</InputLabel>
                <Select
                  value={newMeal.mealType}
                  onChange={(e) => handleMealChange('mealType', e.target.value)}
                >
                  {mealTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newMeal.active}
                      onChange={(e) => handleMealChange('active', e.target.checked)}
                    />
                  }
                  label="Aktif"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Ana Yemek"
                value={newMeal.mainDish}
                onChange={(e) => handleMealChange('mainDish', e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Yardımcı Yemek"
                value={newMeal.sideDish}
                onChange={(e) => handleMealChange('sideDish', e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Çorba"
                value={newMeal.soup}
                onChange={(e) => handleMealChange('soup', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Salata"
                value={newMeal.salad}
                onChange={(e) => handleMealChange('salad', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Tatlı"
                value={newMeal.dessert}
                onChange={(e) => handleMealChange('dessert', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="İçecek"
                value={newMeal.drink}
                onChange={(e) => handleMealChange('drink', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Kalori"
                value={newMeal.calories}
                onChange={(e) => handleMealChange('calories', e.target.value)}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Fiyat (TL)"
                value={newMeal.price}
                onChange={(e) => handleMealChange('price', e.target.value)}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notlar"
                multiline
                rows={3}
                value={newMeal.notes}
                onChange={(e) => handleMealChange('notes', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveMeal}
                  startIcon={<AddIcon />}
                >
                  {editingMeal ? 'Güncelle' : 'Kaydet'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCloseMealDialog}
                >
                  İptal
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MealMenu;
